import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import { employerAPI } from 'utils/api';

const NewSpecialQuota = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [form, setForm] = useState({
    nationality: 'Bangladesh',
    quotaReferenceNo: '',
    quotaApproved: '',
    approvedDate: '',
    remarks: ''
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const quotaRefIsValid = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    // Allow letters, numbers, spaces, slash, dash, dot and parentheses.
    // Must start with an alphanumeric; may end with an alphanumeric or ")".
    return /^[A-Za-z0-9](?:[A-Za-z0-9/\-.()\s]*[A-Za-z0-9)])?$/.test(trimmed);
  };

  const approvedDateIsValid = (value) => {
    if (typeof value !== 'string') return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const [year, month, day] = value.split('-').map(Number);
    if (year < 2000 || year > 2100) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return false;
    return (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() + 1 === month &&
      parsed.getUTCDate() === day
    );
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');

    if (!form.nationality || !form.quotaReferenceNo || !form.quotaApproved || !form.approvedDate) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!quotaRefIsValid(form.quotaReferenceNo)) {
      setError('Quota Reference No. must start with a letter or number, may include letters, numbers, spaces, ".", "/", "-", "(" and ")", and must end with a letter, number or ")".');
      return;
    }

    if (!approvedDateIsValid(form.approvedDate)) {
      setError('Approved Date must be a valid date in YYYY-MM-DD format.');
      return;
    }

    try {
      setSubmitting(true);
      let slipPayload = {};

      if (file) {
        setUploading(true);
        try {
          const uploadResult = await employerAPI.uploadSpecialQuotaSlip(file);
          slipPayload = {
            quota_slip_key: uploadResult?.key || uploadResult?.fileKey,
            quota_slip_url: uploadResult?.url || uploadResult?.signedUrl
          };
        } catch (uploadErr) {
          console.error('Slip upload failed', uploadErr);
          setError(uploadErr.response?.data?.error || uploadErr.message || 'Failed to upload quota slip.');
          setUploading(false);
          setSubmitting(false);
          return;
        }
        setUploading(false);
      }

      const payload = {
        nationality: 'Bangladesh',
        quota_reference_no: form.quotaReferenceNo.trim(),
        quota_approved: Number(form.quotaApproved),
        approved_date: form.approvedDate,
        remarks: form.remarks || undefined,
        ...slipPayload
      };

      const created = await employerAPI.createSpecialQuota(payload);
      const targetVdrId = created?.vdrId || created?.id || created?.data?.vdrId || created?.data?.id;

      // Auto-submit to admin review
      if (targetVdrId) {
        await employerAPI.submitSpecialQuota(targetVdrId);
      }

      setSuccessMessage('Special quota created and submitted for review.');
      if (targetVdrId) {
        navigate(`/employer-special-quota/${targetVdrId}`, { replace: true });
      }
    } catch (err) {
      console.error('Failed to create special quota', err);
      setError(err.response?.data?.error || err.message || 'Failed to create special quota.');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || 'Employer'}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main className={`transition-all duration-300 ease-smooth pt-16 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-6 max-w-5xl mx-auto space-y-6">
          <BreadcrumbNavigation userRole="employer" />

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">New Special Quota</h1>
              <p className="text-text-secondary">Create an eQuotaSpecial VDR and submit it for admin review.</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center px-3 py-2 text-text-secondary hover:text-primary transition-colors"
            >
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back
            </button>
          </div>

          {(error || successMessage) && (
            <div className={`${error ? 'bg-error-50 border-error-200 text-error' : 'bg-success-50 border-success-200 text-success'} border rounded-lg p-4`}>
              {error || successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Nationality<span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  name="nationality"
                  value="Bangladesh"
                  disabled
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg bg-secondary-50 text-text-secondary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Quota Reference No.<span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  name="quotaReferenceNo"
                  value={form.quotaReferenceNo}
                  onChange={handleChange}
                  placeholder="Enter reference number"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  No. of Quota Approved<span className="text-error">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  name="quotaApproved"
                  value={form.quotaApproved}
                  onChange={handleChange}
                  placeholder="Enter number"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Approved Date<span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  name="approvedDate"
                  value={form.approvedDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Upload Quota Slip
                </label>
                <div className="flex items-center space-x-3">
                  <label className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg cursor-pointer hover:bg-primary-700 transition-colors">
                    <Icon name="UploadCloud" size={16} className="mr-2" />
                    {file ? 'Change file' : 'Choose file'}
                    <input type="file" className="hidden" onChange={handleFileChange} />
                  </label>
                  {file && <span className="text-sm text-text-secondary truncate max-w-xs">{file.name}</span>}
                  {uploading && <Icon name="Loader" size={18} className="animate-spin text-primary" />}
                </div>
                <p className="text-xs text-text-tertiary mt-2">PDF or image files. Kept private; admin will see it during review.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Optional notes for admin"
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-100">
              <button
                type="button"
                onClick={() => navigate('/employer-application-management')}
                className="px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
              >
                {submitting && <Icon name="Loader" size={16} className="animate-spin mr-2" />}
                Submit for Review
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewSpecialQuota;
